import React, {
  useEffect, useState, useImperativeHandle, useRef, forwardRef,
} from "react";

import { Popover } from "antd";

import Calendar from "./Calendar";
import DatePickerDisplay from "./DatePickerDisplay";

import "./DatePicker.css";

const DatePicker = forwardRef(({
  showCalendar,
  setShowCalendar = () => { },
  prevValue,
  setPrevValue = () => { },
  value,
  onChange = () => { },
  handleSearchDate = () => { },
  getReportDates = () => [],
  searchOnClear = false,
  useLocalTime = false,
  popoverPlacement = "bottomLeft",
  popoverClassName = "",
  calendarClassName = "",
  disableSingleDaySelection = false, // only allow ranges of at least 2 days
  disableClickAway = false,
  getPopupContainer,
  autoAdjustOverflow = true,
  anchorOverlayToTrigger = false,
  // allowFutureDateSelect = false,
  allowClear = true,
  showClearBtn = true,
  mode,
  dateFormat,
  minDate,
  maxDate,
  datePickerDisplayProps = {
    displayClassName: "",
    showText: true,
    displayTextPosition: "right",
    iconTooltipTitle: "",
    displayElemId: undefined,
  },
  children,
}, ref) => {
  const [showCalendarInternal, setShowCalendarInternal] = useState(false);
  const [dateSelected, setDateSelected] = useState(value ?? "");
  const [prevDateSelected, setPrevDateSelected] = useState(prevValue ?? "");
  const pickerRef = useRef();
  const triggerRef = useRef(null);
  const showCalendarFromParent = useRef(false);
  const valueFromParent = useRef(false);
  const prevValueFromParent = useRef(false);
  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    open: () => setShowCalendarInternal(true),
    close: () => setShowCalendarInternal(false),
  }));

  /** Set showCalendarInternal when value from parent change. */
  useEffect(() => {
    if (showCalendar !== undefined) { // control from parent
      if (showCalendar !== showCalendarInternal) {
        showCalendarFromParent.current = true;
        setShowCalendarInternal(showCalendar);
      }
    }
  }, [showCalendar]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Set dateSelected when value from parent change. */
  useEffect(() => {
    if (value !== undefined) { // control from parent
      if (value !== dateSelected) {
        valueFromParent.current = true;
        setDateSelected(value);
      }
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Set prevDateSelected when value from parent change. */
  useEffect(() => {
    if (prevValue !== undefined) { // control from parent
      if (prevValue !== prevDateSelected) {
        prevValueFromParent.current = true;
        setPrevDateSelected(prevValue);
      }
    }
  }, [prevValue]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Update showCalendar value in parent */
  useEffect(() => {
    if (showCalendarFromParent.current) {
      showCalendarFromParent.current = false;
      return;
    }
    setShowCalendar(showCalendarInternal);
  }, [showCalendarInternal]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Call onDateChange when dateSelectedInternal changes */
  useEffect(() => {
    if (valueFromParent.current) {
      valueFromParent.current = false;
      return;
    }
    onChange(dateSelected);
  }, [dateSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Update prevValue in parent */
  useEffect(() => {
    if (prevValueFromParent.current) {
      prevValueFromParent.current = false;
      return;
    }
    setPrevValue(prevDateSelected);
  }, [prevDateSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = () => {
    setDateSelected(prevDateSelected);
  };

  /** Handle open/closing calendar popover w/ and w/out clickaway. */
  const onOpenChange = (isOpen) => {
    if (isOpen) {
      setPrevDateSelected(dateSelected);
      setShowCalendarInternal(true);
    } else if (!disableClickAway) {
      handleCancel();
      setShowCalendarInternal(isOpen);
    }
  };

  /** COA create report — anchor centered overlay to calendar icon, no page scroll */
  useEffect(() => {
    if (!anchorOverlayToTrigger || !showCalendarInternal) {
      return undefined;
    }

    const positionOverlay = () => {
      const triggerEl = triggerRef.current;
      const popoverEl = document.querySelector(".CalendarPopover.CalendarPopover--coaReport");
      if (!triggerEl || !popoverEl) {
        return;
      }

      const triggerRect = triggerEl.getBoundingClientRect();
      const popoverWidth = 330;
      const viewportPadding = 8;
      const gap = 8;

      let left = triggerRect.left + (triggerRect.width / 2) - (popoverWidth / 2);
      left = Math.max(viewportPadding, Math.min(left, window.innerWidth - popoverWidth - viewportPadding));

      const popoverHeight = popoverEl.getBoundingClientRect().height || 441;
      let top = triggerRect.bottom + gap;
      if (top + popoverHeight > window.innerHeight - viewportPadding) {
        top = triggerRect.top - popoverHeight - gap;
      }
      top = Math.max(viewportPadding, Math.min(top, window.innerHeight - popoverHeight - viewportPadding));

      popoverEl.style.setProperty("position", "fixed", "important");
      popoverEl.style.setProperty("left", `${left}px`, "important");
      popoverEl.style.setProperty("top", `${top}px`, "important");
      popoverEl.style.setProperty("transform", "none", "important");
      popoverEl.style.setProperty("margin", "0", "important");
    };

    const rafId = requestAnimationFrame(positionOverlay);
    window.addEventListener("resize", positionOverlay);
    window.addEventListener("scroll", positionOverlay, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", positionOverlay);
      window.removeEventListener("scroll", positionOverlay, true);
    };
  }, [anchorOverlayToTrigger, showCalendarInternal]);

  const triggerContent = children ?? (
    <>
      <DatePickerDisplay
        dateSelected={dateSelected}
        showCalendarInternal={showCalendarInternal}
        setDateSelected={setDateSelected}
        setPrevDateSelected={setPrevDateSelected}
        handleSearchDate={handleSearchDate}
        allowClear={allowClear}
        {...datePickerDisplayProps} // eslint-disable-line react/jsx-props-no-spreading
      />
    </>
  );

  return (
    <Popover
      ref={pickerRef}
      title=""
      arrow={false}
      autoAdjustOverflow={anchorOverlayToTrigger ? false : autoAdjustOverflow}
      placement={popoverPlacement}
      getPopupContainer={getPopupContainer}
      trigger="click"
      open={showCalendarInternal}
      onOpenChange={onOpenChange}
      destroyOnHidden
      classNames={{ root: `CalendarPopover ${popoverClassName}` }}
      content={(
        <Calendar
          setShowCalendarInternal={setShowCalendarInternal}
          handleSearchDate={handleSearchDate}
          handleCancel={handleCancel}
          getReportDates={getReportDates}
          dateSelected={dateSelected}
          setDateSelected={setDateSelected}
          setPrevDateSelected={setPrevDateSelected}
          searchOnClear={searchOnClear}
          calendarClassName={calendarClassName}
          disableSingleDaySelection={disableSingleDaySelection}
          useLocalTime={useLocalTime}
          showClearBtn={showClearBtn}
          allowClear={allowClear}
          mode={mode}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
    >
      {anchorOverlayToTrigger ? (
        <span ref={triggerRef} className="DatePicker__Trigger DatePicker__Trigger--anchored">
          {triggerContent}
        </span>
      ) : triggerContent}
    </Popover>
  );
});

export default DatePicker;
